import { useState } from 'react';
import { useEffectDelay } from '../useEffectDelay';

interface UseLogStatsOptions {
  apiBaseUrl: string;
  gameKey: string;
  option: string;
  since: string;
  gameResult: 'win' | 'lose';
  deps: unknown[];
}

/**
 * Type representing the response of the useLogStats hook.
 * @typedef {Object} UseLogStatsResponse
 * @property {boolean} loading - Indicates if the request is currently in progress.
 * @property {string[]} logs - Array of log date retrieved from the API.
 * @property {string | null} error - Error message if the request fails, otherwise null.
 */
type UseLogStatsResponse = {
  loading: boolean;
  logs: string[];
  error: string | null;
};

/**
 * Custom React hook for fetching log statistics from the specified API.
 *
 * @param {string} [apiBaseUrl='https://play.hey.gg'] - The base URL of the API to fetch data from.
 * @param {string} gameKey - The key for the game to retrieve statistics for.
 * @param {string} option - The game level.
 * @param {string} since - The start date for retrieving logs.
 * @returns {UseLogStatsResponse} The loading state, log data, and any error message.
 */
export const useLogStats = ({
  apiBaseUrl = 'https://play.hey.gg',
  gameKey,
  option,
  since,
  gameResult,
  deps,
}: UseLogStatsOptions): UseLogStatsResponse => {
  const [loading, setLoading] = useState(false);
  const [logs, setLogs] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffectDelay(
    () => {
      setLoading(true);
      setLogs([]);
      setError(null);

      const accessToken = localStorage.getItem('heygg_jwt');

      // If there is no access token, don't make the fetch call
      if (!accessToken) {
        setLoading(false);
        return;
      }

      fetch(
        `${apiBaseUrl}/api/stats-log?game_key=${gameKey}&option=${option}&since=${since}&game_result=${gameResult}`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            'Content-Type': 'application/json',
          },
        },
      )
        .then((response) => response.json())
        .then((data) => {
          setLogs(data.dates);
        })
        .catch((error) => {
          setError((error as Error).message);
        })
        .finally(() => {
          setLoading(false);
        });
    },
    deps,
    3000,
  );

  return { loading, logs, error };
};
